import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { checkCoverage, isValidUsZip } from '../../lib/coverage';
import { useSettings } from '../../lib/settings';
import { Icons } from '../ui/Icons';

export function Hero() {
  const { openLeadForm } = useApp();
  const settings = useSettings();
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 80]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -40]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-blush pt-10 sm:pt-16">
      {/* Warm ambient shapes */}
      <div className="pointer-events-none absolute inset-0 bg-grain opacity-70" />
      <div className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-terracotta/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 top-40 h-80 w-80 rounded-full bg-sage/10 blur-3xl" />

      <div className="container-rosa relative grid items-center gap-12 pb-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8 lg:pb-24">
        {/* Left column — editorial */}
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage/8 px-4 py-1.5"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-sage" />
            </span>
            <span className="font-body text-[13px] font-semibold text-sage-deep">Every specialist is CAPS-certified &amp; background-checked</span>
          </motion.div>

          <h1 className="mt-6 font-display text-[2.6rem] font-bold leading-[1.04] tracking-[-0.01em] text-plum sm:text-6xl lg:text-[4.1rem]">
            <AnimatedHeadline text={settings.hero_headline} />
          </h1>

          <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-plum/75">{settings.hero_subhead}</p>

          {/* ZIP coverage widget */}
          <div className="mt-8 max-w-lg">
            <ZipChecker onStart={(zip) => openLeadForm({ zip })} ctaLabel={settings.primary_cta_label} />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
            {[
              { icon: 'heart' as const, label: 'Free & no obligation' },
              { icon: 'shield' as const, label: 'Never sold to five companies' },
              { icon: 'clock' as const, label: `Usually within ${settings.response_window_hours} hours` },
            ].map((t) => (
              <span key={t.label} className="flex items-center gap-1.5 font-body text-sm text-plum/65">
                {(() => {
                  const I = Icons[t.icon];
                  return <I width={17} height={17} className="text-terracotta" />;
                })()}
                {t.label}
              </span>
            ))}
          </div>
        </div>

        {/* Right column — documentary image with floating cards */}
        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
          <motion.div style={{ y: imgY }} className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-terracotta/20 via-transparent to-sage/20 blur-xl" />
            <div className="relative overflow-hidden rounded-[1.75rem] border-4 border-white shadow-lift">
              <img
                src="/images/hero-care.webp"
                alt="An adult daughter sitting close beside her mother at a sunlit kitchen table"
                className="aspect-[4/5] w-full object-cover sm:aspect-[5/6]"
                width={720}
                height={864}
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-plum/25 via-transparent to-transparent" />
            </div>
          </motion.div>

          {/* Floating CAPS card */}
          <motion.div
            style={{ y: badgeY }}
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="absolute -left-3 top-8 flex items-center gap-3 rounded-2xl border border-stone bg-white/95 p-3 shadow-lift backdrop-blur sm:-left-8"
          >
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-sage/15 text-sage-deep">
              <Icons.badge width={24} height={24} />
            </span>
            <div>
              <p className="font-display text-sm font-bold leading-none text-plum">CAPS-certified</p>
              <p className="mt-1 font-body text-xs text-plum/55">Vetted &amp; background-checked</p>
            </div>
          </motion.div>

          {/* Floating match card */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="absolute -bottom-4 right-0 flex items-center gap-3 rounded-2xl border border-stone bg-white/95 p-3 pr-4 shadow-lift backdrop-blur sm:-right-6"
          >
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-terracotta/12 font-display text-lg font-bold text-terracotta">
              1:1
            </span>
            <div>
              <p className="font-display text-sm font-bold leading-none text-plum">One specialist</p>
              <p className="mt-1 font-body text-xs text-plum/55">Never an auction</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function AnimatedHeadline({ text }: { text: string }) {
  // Emphasise the word "safe" if present, in terracotta with a drawn underline.
  const parts = text.split(/(safe)/i);
  return (
    <>
      {parts.map((p, i) =>
        /^safe$/i.test(p) ? (
          <span key={i} className="relative whitespace-nowrap text-terracotta">
            {p}
            <motion.span
              className="absolute -bottom-1 left-0 h-[3px] w-full origin-left rounded-full bg-terracotta/40"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            />
          </span>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

function ZipChecker({ onStart, ctaLabel }: { onStart: (zip: string) => void; ctaLabel: string }) {
  const [zip, setZip] = useState('');
  const [state, setState] = useState<'idle' | 'checking' | 'covered'>('idle');
  const [area, setArea] = useState('your area');
  const [error, setError] = useState('');

  const run = async () => {
    if (!isValidUsZip(zip)) {
      setError('Enter a 5-digit ZIP code');
      return;
    }
    setError('');
    setState('checking');
    const res = await checkCoverage(zip);
    setArea(res.area);
    setState('covered');
  };

  return (
    <div className="rounded-3xl border border-stone bg-white/80 p-4 shadow-soft backdrop-blur sm:p-5">
      {state !== 'covered' ? (
        <>
          <p className="mb-2.5 flex items-center gap-2 font-body text-sm font-semibold text-plum">
            <Icons.pin width={17} height={17} className="text-terracotta" /> Is Rosa near you? Enter your ZIP to begin.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <input
                value={zip}
                onChange={(e) => {
                  setZip(e.target.value.replace(/\D/g, '').slice(0, 5));
                  setError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && run()}
                inputMode="numeric"
                placeholder="e.g. 43082"
                aria-label="ZIP code"
                className={`w-full rounded-2xl border bg-white px-4 py-3.5 font-body text-plum outline-none transition-colors placeholder:text-plum/40 ${
                  error ? 'border-terracotta' : 'border-stone focus:border-terracotta focus:shadow-glow'
                }`}
              />
            </div>
            <button onClick={run} disabled={state === 'checking'} className="btn-primary shrink-0 !py-3.5">
              {state === 'checking' ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-blush/40 border-t-blush" /> Checking
                </>
              ) : (
                <>
                  <Icons.search width={18} height={18} /> Check my area
                </>
              )}
            </button>
          </div>
          {error && <p className="mt-1.5 font-body text-xs text-terracotta-deep">{error}</p>}
        </>
      ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-sage/15 text-sage-deep">
              <Icons.check width={24} height={24} />
            </span>
            <div className="flex-1">
              <p className="font-display text-lg font-bold text-plum">Good news, we're in {area}.</p>
              <p className="mt-0.5 font-body text-sm text-plum/65">
                CAPS-certified specialists serve ZIP {zip}. Let's get your free assessment scheduled.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button onClick={() => onStart(zip)} className="btn-primary flex-1">
              {ctaLabel} <Icons.arrowRight width={18} height={18} />
            </button>
            <button onClick={() => setState('idle')} className="btn-secondary shrink-0">
              Try another ZIP
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
